/**
 * PortionStep
 *
 * Step 3 of the Generate Week wizard.
 * Allows user to select number of servings per meal.
 * Refactored from the original GenerateModal portion selection.
 */

import React, { useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, TextInput } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Paprika, PaprikaTypography, PaprikaSpacing, PaprikaRadius } from '../../../styles/theme';

const PORTION_OPTIONS = [2, 4, 6];

interface PortionStepProps {
  servings: number;
  onServingsChange: (servings: number) => void;
}

export const PortionStep: React.FC<PortionStepProps> = ({
  servings,
  onServingsChange,
}) => {
  const { t } = useTranslation();
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [customValue, setCustomValue] = useState('');

  const handlePortionSelect = (value: number) => {
    setShowCustomInput(false);
    setCustomValue('');
    onServingsChange(value);
  };

  const handleCustomSelect = () => {
    setShowCustomInput(true);
    setCustomValue('');
  };

  const handleCustomChange = (value: string) => {
    const numericValue = value.replace(/[^0-9]/g, '');
    setCustomValue(numericValue);
    const parsed = parseInt(numericValue);
    if (!isNaN(parsed) && parsed > 0) {
      onServingsChange(parsed);
    }
  };

  const isCustomSelected = showCustomInput || (servings > 0 && !PORTION_OPTIONS.includes(servings));

  return (
    <View style={styles.container}>
      {/* Header */}
      <Text style={styles.header}>{t('wizard.portionTitle')}</Text>
      <Text style={styles.subtitle}>
        {t('wizard.portionSubtitle')}
      </Text>

      {/* Portion Options */}
      <View style={styles.portionGrid}>
        {PORTION_OPTIONS.map((option) => {
          const isSelected = servings === option && !showCustomInput;

          return (
            <TouchableOpacity
              key={option}
              style={[
                styles.portionButton,
                isSelected && styles.portionButtonSelected,
              ]}
              onPress={() => handlePortionSelect(option)}
            >
              <Text
                style={[
                  styles.portionNumber,
                  isSelected && styles.portionNumberSelected,
                ]}
              >
                {option}
              </Text>
              <Text
                style={[
                  styles.portionLabel,
                  isSelected && styles.portionLabelSelected,
                ]}
              >
                {t('wizard.servings')}
              </Text>
            </TouchableOpacity>
          );
        })}

        {/* Custom Button */}
        <TouchableOpacity
          style={[
            styles.portionButton,
            isCustomSelected && styles.portionButtonSelected,
          ]}
          onPress={handleCustomSelect}
        >
          <Text
            style={[
              styles.portionNumber,
              isCustomSelected && styles.portionNumberSelected,
            ]}
          >
            {isCustomSelected && servings > 0 && !PORTION_OPTIONS.includes(servings)
              ? servings
              : '?'}
          </Text>
          <Text
            style={[
              styles.portionLabel,
              isCustomSelected && styles.portionLabelSelected,
            ]}
          >
            {t('wizard.custom')}
          </Text>
        </TouchableOpacity>
      </View>

      {/* Custom Input */}
      {showCustomInput && (
        <View style={styles.customInputContainer}>
          <TextInput
            style={styles.customInput}
            placeholder={t('wizard.enterServings')}
            placeholderTextColor={Paprika.textMuted}
            keyboardType="number-pad"
            value={customValue}
            onChangeText={handleCustomChange}
            autoFocus
          />
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Paprika.background,
    padding: PaprikaSpacing.lg,
  },

  // Header
  header: {
    ...PaprikaTypography.headingMedium,
    color: Paprika.textPrimary,
    textAlign: 'center',
    marginBottom: PaprikaSpacing.xs,
  },
  subtitle: {
    ...PaprikaTypography.bodyMedium,
    color: Paprika.textSecondary,
    textAlign: 'center',
    marginBottom: PaprikaSpacing.xl,
  },

  // Portion Grid
  portionGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: PaprikaSpacing.md,
    justifyContent: 'center',
  },

  // Portion Button
  portionButton: {
    width: 80,
    height: 80,
    borderRadius: PaprikaRadius.lg,
    borderWidth: 1,
    borderColor: Paprika.border,
    backgroundColor: Paprika.background,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 2,
  },
  portionButtonSelected: {
    backgroundColor: Paprika.textPrimary,
    borderColor: Paprika.textPrimary,
  },
  portionNumber: {
    ...PaprikaTypography.headingLarge,
    color: Paprika.textPrimary,
  },
  portionNumberSelected: {
    color: Paprika.background,
  },
  portionLabel: {
    ...PaprikaTypography.bodySmall,
    color: Paprika.textSecondary,
  },
  portionLabelSelected: {
    color: Paprika.background,
  },

  // Custom Input
  customInputContainer: {
    marginTop: PaprikaSpacing.lg,
  },
  customInput: {
    ...PaprikaTypography.bodyMedium,
    borderWidth: 1,
    borderColor: Paprika.border,
    borderRadius: PaprikaRadius.md,
    paddingVertical: PaprikaSpacing.sm,
    paddingHorizontal: PaprikaSpacing.md,
    textAlign: 'center',
    color: Paprika.textPrimary,
  },
});

export default PortionStep;
